// Actions
import { BuilderGoSettings, StringOptionsT } from 'Utils/apiTypes';

export const LOAD_CONFIG = 'app/App/LOAD_CONFIG';
export const LOAD_CONFIG_SUCCESS = 'app/App/LOAD_CONFIG_SUCCESS';
export const LOAD_CONFIG_FAILURE = 'app/App/LOAD_CONFIG_FAILURE';
export const HANDLE_UNLOCK_MAIN_NAV = 'app/App/HANDLE_UNLOCK_MAIN_NAV';
export const HANDLE_UNLOCK_SUB_NAV = 'app/App/HANDLE_UNLOCK_SUB_NAV';
export const SET_PAGE_VIEWED = 'app/App/SET_PAGE_VIEWED';
export const CHOOSE_PRODUCT = 'app/App/CHOOSE_PRODUCT';
export const HANDLE_PROFILE_COMPLETE = 'app/App/HANDLE_PROFILE_COMPLETE';

// Page Slugs
export const PROFILE_SLUG = 'profile';
export const RESULT_SLUG = 'results';
export const INTRO_SLUG = 'intro';
export const VIEW_PLAN_SLUG = 'plan';
export const VIEW_BENEFIT_SLUG = 'benefit';
export const ERROR_SLUG = 'error';
export const OVERVIEW_SLUG = 'overview';
export const REVIEW_SLUG = 'review';
export const HSA_PLUS_SLUG = 'hsa';
export const TAX_SAVINGS_SLUG = 'tax-savings';
export const CONFIG_SLUG = 'config';
export const MTM_SLUG = 'moments-that-matter';
export const ASK_ALEX_SLUG = 'ask-alex';

/**
 * Used by utils to strip page slugs from a URL so we can correctly
 * parse customer slug and segment slug if applicable.
 * If we add a new page, add the new slug as a constant and add it to this array
 */
export const PAGE_SLUGS = [
  INTRO_SLUG,
  PROFILE_SLUG,
  RESULT_SLUG,
  HSA_PLUS_SLUG,
  TAX_SAVINGS_SLUG,
  VIEW_PLAN_SLUG,
  OVERVIEW_SLUG,
  VIEW_BENEFIT_SLUG,
  MTM_SLUG,
  REVIEW_SLUG,
  CONFIG_SLUG,
  ASK_ALEX_SLUG,
  ERROR_SLUG,
];

export const NETWORK_TYPE_ID = {
  INDEMNITY: '1',
  PPO: '2',
  PREFERRED_PPO: '3',
  HMO: '4',
  PREFERRED_IN_NETWORK_ONLY: '5',
} as const;

// Plans with these network type IDs have out-of-network coverage (PPO, Preferred PPO)
export const OON_NETWORK_TYPE_IDS: ReadonlySet<string> = new Set([
  NETWORK_TYPE_ID.PPO,
  NETWORK_TYPE_ID.PREFERRED_PPO,
]);

// Plans with these network type IDs have a Most Preferred network tier (Preferred PPO and Preferred In-Network Only)
export const MP_NETWORK_TYPE_IDS: ReadonlySet<string> = new Set([
  NETWORK_TYPE_ID.PREFERRED_PPO,
  NETWORK_TYPE_ID.PREFERRED_IN_NETWORK_ONLY,
]);

export enum COVERAGE_TIERS {
  INDIVIDUAL = 'individual',
  INDIVIDUAL_SPOUSE = 'individual_spouse',
  INDIVIDUAL_CHILD = 'individual_child',
  INDIVIDUAL_CHILDREN = 'individual_children',
  FAMILY = 'family',
}

// Tiers
export const INDIVIDUAL_TIER = COVERAGE_TIERS.INDIVIDUAL;
export const COUPLE_TIER = COVERAGE_TIERS.INDIVIDUAL_SPOUSE;
export const INDIVIDUAL_DEPENDANT_TIER = COVERAGE_TIERS.INDIVIDUAL_CHILD;
export const FAMILY_TIER = COVERAGE_TIERS.FAMILY;

// Low Population ZIP3
export const LOW_POP_ZIP3 = [
  '036',
  '059',
  '063',
  '102',
  '203',
  '556',
  '692',
  '790',
  '821',
  '823',
  '830',
  '831',
  '878',
  '879',
  '884',
  '890',
  '893',
];

// Filing statuses
export const SINGLE = 'single';
export const MARRIED = 'married';
export const MARRIED_FILING_SEPARATELY = 'married_separately';
export const HEAD_OF_HOUSEHOLD = 'head_of_household';

// JVP Actions

// Bootstrap
export const GET_JVP_BOOTSTRAP = 'app/JVP/GET_JVP_BOOTSTRAP';
export const GET_JVP_BOOTSTRAP_SUCCESS = 'app/JVP/GET_JVP_BOOTSTRAP_SUCCESS';
export const GET_JVP_BOOTSTRAP_FAILURE = 'app/JVP/GET_JVP_BOOTSTRAP_FAILURE';

export const SET_MEDICARE_DESTINATION = 'app/JVP/SET_MEDICARE_DESTINATION';

// JVP User
export const GET_JVP_USER_SUCCESS = 'app/JVP/GET_JVP_USER_SUCCESS';
export const GET_JVP_USER_FAILURE = 'app/JVP/GET_JVP_USER_FAILURE';

export const SET_ENROLLMENT_EVENT_TYPE = 'app/JVP/SET_ENROLLMENT_EVENT_TYPE';
export const SET_ENROLLMENT_EVENT_TYPE_SUCCESS = 'app/JVP/SET_ENROLLMENT_EVENT_TYPE_SUCCESS';
export const SET_ENROLLMENT_EVENT_TYPE_FAILURE = 'app/JVP/SET_ENROLLMENT_EVENT_TYPE_FAILURE';

export const SET_ENROLLMENT_EVENT_PROFILE_COMPLETE = 'app/JVP/SET_ENROLLMENT_EVENT_PROFILE_COMPLETE';
export const SET_ENROLLMENT_EVENT_PROFILE_COMPLETE_SUCCESS =
  'app/JVP/SET_ENROLLMENT_EVENT_PROFILE_COMPLETE_SUCCESS';
export const SET_ENROLLMENT_EVENT_PROFILE_COMPLETE_FAILURE =
  'app/JVP/SET_ENROLLMENT_EVENT_PROFILE_COMPLETE_FAILURE';

export const UPDATE_USER_UI_SETTINGS = 'app/JVP/UPDATE_USER_UI_SETTINGS';

export const GO_GUEST_ID = 'go_guest_id';
export const GO_ENROLLMENT_EVENT_ID = 'go_enrollment_event_id';

export const CONFIG_POLLING_INTERVAL_MS = 1000 * 60;

export const DEFAULT_IDLE_TIME_LENGTH = 30 * 1000 * 60;
export const IDLE_TIME_LENGTH_FOR_STAGING = 1 * 1000 * 60;

// Integrated User Session Management
// AAPI expires a session after 10 idle minutes. We pulse every minute so a single dropped
// request (or one slow tick) can't cost us the session, and so we detect an expired session
// (401) promptly instead of pinging a dead session forever.
export const JVP_SESSION_PULSE_TIME = 1 * 1000 * 60;
export const SET_JVP_SESSION_ID = 'app/JVP/SET_JVP_SESSION_ID';
export const PULSE_JVP_SESSION = 'app/JVP/PULSE_JVP_SESSION';
// Pulse returned a 401 -> the AAPI session has expired. Terminal: stop pulsing, show the modal.
// Any other non-200 keeps the existing retry behavior (the 1-minute interval just tries again).
export const JVP_SESSION_EXPIRED = 'app/JVP/JVP_SESSION_EXPIRED';
export const GET_AAPI_USER_REQUEST = 'app/AAPI/GET_AAPI_USER_REQUEST';
export const GET_AAPI_USER_SUCCESS = 'app/AAPI/GET_AAPI_USER_SUCCESS';
export const GET_AAPI_USER_FAILURE = 'app/AAPI/GET_AAPI_USER_FAILURE';

export const SET_ALEX_ID_UUID = 'app/SET_ALEX_ID_UUID';

export const SET_DEMO_MODE = 'app/SET_DEMO_MODE';
export const SET_UNVALIDATED_SEGMENT_SLUG = 'app/SET_UNVALIDATED_SEGMENT_SLUG';
export const SET_BC_URL_HASH = 'app/SET_BC_URL_HASH';
export const SET_VALIDATED_SEGMENT_SLUG = 'app/SET_VALIDATED_SEGMENT_SLUG';

// Returning user management
export const UPDATE_ELECTIONS = 'app/UPDATE_ELECTIONS';

export const DEFAULT_BUILDER_GO_SETTINGS: BuilderGoSettings = {
  alex_go_language: 'english',
  claims_data_enabled: true,
  show_accident_video: true,
  show_commuter_video: true,
  show_dcfsa_video: true,
  show_dental_video: true,
  show_eap_video: true,
  show_financial_wellness_video: true,
  show_fsa_video: true,
  show_hospital_indemnity_video: true,
  show_hra_video: true,
  show_hsa_video: true,
  show_legal_video: true,
  show_lpfsa_video: true,
  show_ltd_video: true,
  show_retirement_video: true,
  show_std_video: true,
  show_telemedicine_video: true,
  show_vision_video: true,
  show_wellness_video: true,
  show_add_video: true,
  show_critical_illness_video: true,
  show_group_life_video: true,
  kill_med_rec: false,
  use_contribution_term: false,
  accident_no_premiums: true,
  has_teladoc_support_page: false,
  health_outcomes_vendor_sign_up_code: '',
  health_outcomes_teladoc_url: '#',
  has_talkspace_support_page: false,
  hsa_funds_oop_cost: false,
};

export const NO_DEDUCTIBLE_PLAN_CARRIERS = ['Coupe', 'SimplePay', 'Surest'];

export const BEST_FIT_SCORE = 99;
export const GREAT_FIT_SCORE = 90;
export const FAIR_FIT_SCORE = 75;

// Define Plan Fit Labels
const labelLocation = 'health_section.recommendation.plan_fit_labels';
export const labelOptions: StringOptionsT = {
  best: `${labelLocation}.best_fit_label`,
  great: `${labelLocation}.great_fit_label`,
  fair: `${labelLocation}.fair_fit_label`,
  poor: `${labelLocation}.poor_fit_label`,
};

export const claimsErrorState = [
  'invalid_user_input',
  'timeout',
  'provider_down',
  'provider_gone',
  'rate_limited',
  'invalid_data',
];

/*
These are the plans for which Partners Benefits are available in Builder.
We omitted medical plan as its not needed on supplemental page.
*/
export const availablePartnerInsuranceTypes = [
  'ltc',
  'dental',
  'life',
  'vision',
  'accident',
  'hospital',
  'cancer',
  'critical',
  'add',
];
